/**
 * ★★ 附件限额 · 真本（全站唯一一份）★★
 *
 * ════════ 这个文件为什么必须存在 ════════
 *
 * 2026-09-03 协会《优化内容9.3》「佐证材料不限制数量，应允许双方提交录音、视频等」，
 * 主公按 A 方案拍板放开限额时，同一组数字在仓里**散着写了七遍**：
 *
 *   · `server/src/modules/cases/case-attachments.ts`      MAX_ATTACHMENTS / MAX_ATTACHMENT_BYTES
 *   · `server/src/modules/citizen/citizen.service.ts`     另一份同名常量
 *   · `shared/src/respondent-contract.ts`                 RESPONDENT_DEFENSE_LIMITS
 *   · `shared/src/amend-contract.ts`                      CITIZEN_AMEND_LIMITS
 *   · `web/src/components/AttachmentPicker.tsx`           MAX_FILES / MAX_BYTES
 *   · `citizen/src/util/complaint-rules.ts`               MAX_FILES / MAX_FILE_BYTES / SERVER_BODY_LIMIT
 *   · `server/src/main.ts`                                BODY_LIMIT
 *
 * 七处里改漏一处，症状是**界面允许、后端 400**（或反过来「后端允许、界面不让选」），
 * 而这两种都会被当成「系统坏了」。所以数字从此只有这一份，别处一律 import。
 *
 * ════════ 为什么放在 `@cdfjx/shared` 而不是 server 里 ════════
 *
 * 后台前端（`packages/web`）与市民端（`packages/citizen`）都要拿这组数字，
 * 而 `packages/server` 的模块里随便一个值导出就会把 `@nestjs/common` 拖进浏览器包
 * （来龙去脉见 `server/src/modules/cases/contract-v2.ts` 与 `case-attachments.ts` 的文件头）。
 * `@cdfjx/shared` 是两个前端都已经通过 vite alias 指回源码 .ts 的公共上游，
 * 里面只有类型、常量与纯函数 —— 本文件也严守这一条：**不 import 任何 Node 内置模块**，
 * 不出现 `Buffer`，不出现 `fs`。落盘、解码那些事留在各自的服务层。
 *
 * ════════ 2026-09-04 批 ⑭ 的新口径（A 方案，主公拍板）════════
 *
 *   单件上限   10MB  →  40MB
 *   单次件数   9     →  20
 *   请求体闸   32mb  →  64mb   （base64 比原文胖 1/3，40MB 的文件 ≈ 53.4MB）
 *   单案总量   —     →  200MB  （**新增的一道闸**，按该案已存附件累计，见下）
 *   可传类型   图片/文档  →  再加常见音频与视频（录音、手机拍的短视频）
 *
 * ★ 「单案总量」是**累计**，不是「本次合计」：
 *   件数放开之后，只卡「本次」等于没有闸 —— 分二十次传，每次十几件照样能把一件案子
 *   撑到几个 G。所以后端要读该案已存的四份索引（见 `case-attachment-quota.ts`）把历史
 *   用量加进来。**读不到就拒绝**，绝不当成 0 放行。
 *
 * ★ 一次请求实际装得下多少，仍由请求体闸决定，不由这几个数决定：
 *   20 件 × 40MB = 800MB 原文 ≈ 1067MB base64，**远超 64mb 的请求体闸**，
 *   会先撞 body-parser 的 413（英文 `request entity too large`，用户只看到「提交失败」）。
 *   所以前端必须另守一道 `TOTAL_B64_BUDGET_BYTES`，并把话说成「分两次提交」。
 *   正解仍然是附件走 multipart / 直传对象存储，不 base64 塞 JSON —— 那是另一批的事。
 */

// ────────────────────────────────────────────────────────────────────────
// 一、数字
// ────────────────────────────────────────────────────────────────────────

const MB = 1024 * 1024;

export const ATTACHMENT_LIMITS = {
  /** 一次最多传几件 */
  maxAttachments: 20,
  /** 单件最大字节数（**解码后**的原文字节数，不是 base64 串长度） */
  maxAttachmentBytes: 40 * MB,
  /** 单件案子**累计**能存多少（历史已存 ＋ 本次新传） */
  maxCaseTotalBytes: 200 * MB,
  /** Express body-parser 的硬上限 */
  serverBodyLimitBytes: 64 * MB,
  /**
   * 请求体里除附件之外的余量（字段名、原文、引号逗号、HTTP 头）。
   * 卡到 64MB 整会在边界上偶发 413，那种「有时候能提交有时候不能」最难查。
   */
  bodyOverheadBytes: 4 * MB,
} as const;

/**
 * `main.ts` 喂给 body-parser 的那个字符串。
 *
 * **从字节数算出来，不是手写的第二份**：手写就又多一个会漂的地方
 * （改了字节数忘了改字符串 ＝ 闸门纹丝不动，而所有人都以为放开了）。
 */
export const SERVER_BODY_LIMIT_LITERAL = `${ATTACHMENT_LIMITS.serverBodyLimitBytes / MB}mb`;

/**
 * 一次提交里**所有附件 base64 串长度**的预算。前端据此在选文件时就拦住。
 *
 * 60MB 的 base64 ≈ 45MB 原文 —— 也就是说「一次能传一件 40MB 的，或者若干件小的」。
 * 这不是限额口径变了，是请求体闸决定的物理上限，话要对用户说清楚。
 */
export const TOTAL_B64_BUDGET_BYTES =
  ATTACHMENT_LIMITS.serverBodyLimitBytes - ATTACHMENT_LIMITS.bodyOverheadBytes;

/**
 * 同一个预算，换算成**原文**字节数（base64 比原文胖 4/3，所以乘 3/4）。
 *
 * ★★ **凡是给人看的数字一律用这一个，判断仍然用上面那个。** 两者不许混。
 *   道理：物理约束确实在 base64 上（body-parser 卡的是请求体），但用户手上的是文件，
 *   他看到的体积是原文。拿 base64 长度当体积报给他，会出现
 *   「我明明只选了一件 40MB 的，怎么说已选 53.4MB」—— 数字自相矛盾，
 *   人只会以为系统坏了。这个坑本项目 2026-08-26 已经踩过一次同型的
 *   （前端拿 base64 串长度去比单件 10MB，把上限悄悄收成 7.5MB），
 *   当时只改了单件那一处，总量这一处漏了；单件放到 40MB 之后它就藏不住了。
 */
export const TOTAL_RAW_BUDGET_BYTES = Math.floor((TOTAL_B64_BUDGET_BYTES * 3) / 4);

// ────────────────────────────────────────────────────────────────────────
// 二、可传类型白名单
// ────────────────────────────────────────────────────────────────────────

/**
 * ★ **白名单，不是「星杠星」那种全放开**（2026-09-04 批 ⑭ 硬要求）。
 *   「放开录音和视频」不等于「什么都收」：可执行文件、脚本、压缩包一律不进，
 *   因为它们既没有办案价值，又是把附件目录变成投放点的最短路径。
 *
 * ★ 判据是**扩展名，且只有扩展名**（2026-09-05 巡检后收紧的口径）。理由：
 *   ① 落盘的键与下载时浏览器怎么打开，靠的都是扩展名（见 `case-attachments.ts` 的 `staffFileKey`）；
 *   ② 手机浏览器给录音、给 amr / m4a 的 MIME 极不稳定，常常是空串或
 *      `application/octet-stream` —— 只认 MIME 会把真实的录音全部拒掉。
 *
 *   这里原来还有一句「MIME 若给了且不是通用兜底值，也必须在白名单里」，**已废止**：
 *   那道闸把 `image/jpg`（非标准但真实存在）这类合法照片误拒了，报的还是
 *   「.jpg 文件暂不支持」这种自相矛盾的话。它原本承担的安全职责改由
 *   `storedMimeForName()` 承担 —— 落盘的 Content-Type 按扩展名派生，
 *   客户端自称的 MIME 根本进不了存储，比原来更硬。
 */
export interface AttachmentKindSpec {
  readonly kind: AttachmentKind;
  /** 给人看的类别名 */
  readonly label: string;
  readonly mimes: readonly string[];
  /** 一律小写、带点 */
  readonly exts: readonly string[];
}

export type AttachmentKind = 'image' | 'document' | 'audio' | 'video';

export const ATTACHMENT_KINDS: readonly AttachmentKindSpec[] = [
  {
    kind: 'image',
    label: '图片',
    mimes: [
      'image/jpeg',
      'image/pjpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'image/bmp',
      'image/heic',
      'image/heif',
    ],
    exts: ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.heic', '.heif'],
  },
  {
    kind: 'document',
    label: '文档',
    mimes: [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
    ],
    exts: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt'],
  },
  {
    kind: 'audio',
    label: '音频',
    // ★ 录音是本批的主角。同一个 .m4a 在 iOS / 安卓 / 微信里能报出四种 MIME，
    //   所以这一行列得比别处宽 —— 宽的是「认得出」，扩展名那道闸一个都没松。
    mimes: [
      'audio/mpeg',
      'audio/mp3',
      'audio/mp4',
      'audio/m4a',
      'audio/x-m4a',
      'audio/aac',
      'audio/aacp',
      'audio/wav',
      'audio/wave',
      'audio/x-wav',
      'audio/amr',
      'audio/3gpp',
      'audio/ogg',
    ],
    exts: ['.mp3', '.m4a', '.aac', '.wav', '.amr', '.ogg'],
  },
  {
    kind: 'video',
    label: '视频',
    mimes: [
      'video/mp4',
      'video/quicktime',
      'video/3gpp',
      'video/3gpp2',
      'video/x-msvideo',
      'video/x-matroska',
      'video/webm',
    ],
    exts: ['.mp4', '.mov', '.3gp', '.3g2', '.avi', '.mkv', '.webm'],
  },
];

/** 给人看的「能传什么」一句话，前后端同一句。 */
export const ATTACHMENT_KINDS_HINT = ATTACHMENT_KINDS.map((k) => k.label).join('、');

export const ALLOWED_ATTACHMENT_EXTS: readonly string[] = ATTACHMENT_KINDS.flatMap((k) => k.exts);

export const ALLOWED_ATTACHMENT_MIMES: readonly string[] = ATTACHMENT_KINDS.flatMap((k) => k.mimes);

/**
 * 通用兜底 MIME：给了等于没给，一律以扩展名为准。
 * 空串同理（很多安卓浏览器选录音时 `File.type` 就是空的）。
 */
const GENERIC_MIMES: readonly string[] = ['', 'application/octet-stream', 'binary/octet-stream'];

/**
 * ════════ 扩展名 → 我们自己认定的 Content-Type（2026-09-05 巡检后新增）════════
 *
 * **落盘时一律用这张表，不采信客户端自称的 MIME。** 两个理由，都不是洁癖：
 *
 * ① 安全：附件下发是 OSS 预签名 ＋ `Content-Disposition: inline`，而对象的
 *    Content-Type 就是上传那一刻写进去的那个。若采信客户端自称，
 *    传一个 `x.jpg` 却声明 `text/html`，就在 fcomp.cdeaa.com 上挂了一个
 *    点开即执行的页面 —— 存储型 XSS，且域名是我们自己的。
 *    按扩展名派生之后，`.jpg` 永远是 `image/jpeg`，这条路直接断死。
 *
 * ② 正确性：同一个文件在不同机型上报出的 MIME 五花八门（`image/jpg`、
 *    `video/mpeg`、`text/csv`…）。原来拿客户端 MIME 去对白名单，
 *    **合法照片会被误拒**，而且报的还是「.jpg 文件暂不支持」这种
 *    自相矛盾的话（下一句又列着可以传 .jpg）。按扩展名派生就没有变体问题，
 *    也不必再跟各家机型打地鼠。
 *
 * 每个扩展名单列，**不要**图省事回落到所属类别的第一个 MIME ——
 * 那会把 .png 存成 image/jpeg。
 */
const EXT_TO_MIME: Readonly<Record<string, string>> = {
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.bmp': 'image/bmp',
  '.heic': 'image/heic',
  '.heif': 'image/heif',
  '.pdf': 'application/pdf',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xls': 'application/vnd.ms-excel',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.txt': 'text/plain',
  '.mp3': 'audio/mpeg',
  '.m4a': 'audio/mp4',
  '.aac': 'audio/aac',
  '.wav': 'audio/wav',
  '.amr': 'audio/amr',
  '.ogg': 'audio/ogg',
  '.mp4': 'video/mp4',
  '.mov': 'video/quicktime',
  '.3gp': 'video/3gpp',
  '.3g2': 'video/3gpp2',
  '.avi': 'video/x-msvideo',
  '.mkv': 'video/x-matroska',
  '.webm': 'video/webm',
};

/**
 * 落盘该用哪个 Content-Type。**所有 `storage.put()` 都必须走这里**，
 * 不要把 `f.contentType` 直接传下去（理由见上）。
 *
 * 认不出的扩展名回落 `application/octet-stream` —— 浏览器不会执行它，
 * 是这里最安全的落点。注意扩展名白名单在更早的 `checkAttachmentType()`
 * 已经把关，正常路径上走不到这个回落。
 */
export function storedMimeForName(name: string): string {
  return EXT_TO_MIME[attachmentExt(name)] ?? 'application/octet-stream';
}

/**
 * `<input type="file" accept="…">` 的取值。
 *
 * ★ 扩展名与 MIME **都写**：只写 MIME 时安卓相册里的 .amr / .m4a 会被系统灰掉选不了，
 *   只写扩展名时 iOS 的「照片」入口又不认。两个都给才是两端都能选。
 * ★ **不许退化成「星杠星」** —— 那等于前端这道白名单不存在，用户能选到最后才被后端顶回来。
 */
export const ATTACHMENT_ACCEPT_ATTR = [...ALLOWED_ATTACHMENT_EXTS, ...ALLOWED_ATTACHMENT_MIMES]
  .filter((v) => v !== 'image/pjpeg')
  .join(',');

/** 取小写扩展名（含点）。没有扩展名返回空串。 */
export function attachmentExt(name: string): string {
  const base = String(name ?? '')
    .trim()
    .split(/[\\/]/)
    .pop();
  const t = String(base ?? '');
  const dot = t.lastIndexOf('.');
  if (dot <= 0 || dot === t.length - 1) return '';
  return t.slice(dot).toLowerCase();
}

/** 这一件属于哪一类；不在白名单里返回 null。 */
export function attachmentKind(name: string, contentType?: string): AttachmentKind | null {
  const ext = attachmentExt(name);
  for (const k of ATTACHMENT_KINDS) {
    if ((k.exts as readonly string[]).includes(ext)) return k.kind;
  }
  // 扩展名不认得时不猜类别 —— 类别只用于界面显示，猜错等于给人看一个假标签
  const mime = normalizeMime(contentType);
  if (!GENERIC_MIMES.includes(mime)) {
    for (const k of ATTACHMENT_KINDS) {
      if ((k.mimes as readonly string[]).includes(mime)) return k.kind;
    }
  }
  return null;
}

function normalizeMime(contentType?: string): string {
  // `; charset=…` 之类的参数一律砍掉再比。`split` 在 noUncheckedIndexedAccess 下
  // 回的是 `string | undefined`，所以补一个 `?? ''`——不是防御性冗余，是编译要求。
  const head = String(contentType ?? '').split(';')[0] ?? '';
  return head.trim().toLowerCase();
}

// ────────────────────────────────────────────────────────────────────────
// 三、话术（前后端逐字一致，改一处两端同时变）
// ────────────────────────────────────────────────────────────────────────

/**
 * 人话的体积。1024 进制，与操作系统显示一致。
 *
 * ★ 原先这个函数只住在 `citizen/src/util/complaint-rules.ts` 里叫 `fmtSize`，
 *   后端拒绝时另写「超过 10MB」的字面量 —— 于是两端说的话对不上。现在同一份。
 */
export function fmtBytes(bytes: number): string {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)}KB`;
  return `${(n / 1024 / 1024).toFixed(1)}MB`;
}

/** 件数超了。`what` 是这批材料的说法（「证据」「佐证附件」「答辩材料」…）。 */
export function msgTooManyAttachments(what: string, got: number): string {
  return `${what}一次最多上传 ${ATTACHMENT_LIMITS.maxAttachments} 件（本次 ${got} 件），请分两次提交。`;
}

/** 单件超了。 */
export function msgAttachmentTooLarge(what: string, name: string, bytes: number): string {
  /**
   * ★ 2026-09-05 巡检修：`fmtBytes()` 只保留一位小数，紧贴边界的文件
   *   （40MB + 1 字节 ～ 40.05MB）会与上限**显示成同一个数**，报出
   *   「40.0MB，超过单件 40.0MB 的上限」—— 两个数字一样，人只会以为系统坏了。
   *
   *   这里判一眼：两边显示相同就换成精确字节数说话。宁可数字长一点，
   *   也不能让提示自相矛盾。
   */
  const limit = ATTACHMENT_LIMITS.maxAttachmentBytes;
  const same = fmtBytes(bytes) === fmtBytes(limit);
  const mine = same ? `${bytes.toLocaleString('en-US')} 字节` : fmtBytes(bytes);
  const cap = same ? `${limit.toLocaleString('en-US')} 字节` : fmtBytes(limit);
  return (
    `${what}「${name}」${mine}，超过单件 ${cap} 的上限。` +
    `请压缩后再传，或截取其中需要的一段。`
  );
}

/** 类型不在白名单。 */
export function msgAttachmentTypeRejected(what: string, name: string): string {
  const ext = attachmentExt(name);
  return (
    `${what}「${name}」${ext ? `是 ${ext} 文件，` : '没有可识别的文件扩展名，'}` +
    `暂不支持。可以传的是：${ATTACHMENT_KINDS_HINT}（${ALLOWED_ATTACHMENT_EXTS.join('、')}）。`
  );
}

/** 内容为空。 */
export function msgAttachmentEmpty(what: string, name: string): string {
  return `${what}「${name}」内容为空，请重新选择。`;
}

/**
 * 单案累计超了。
 *
 * ★ 硬要求（主公 2026-09-04）：**要能照做** —— 这一件多大、上限多少、还剩多少额度，
 *   三个数一个都不许省。只说「上传失败」等于把人推到客服电话上。
 */
export function msgCaseQuotaExceeded(o: {
  what: string;
  name: string;
  bytes: number;
  /** 该案已存的历史用量 */
  usedBytes: number;
}): string {
  const remain = Math.max(0, ATTACHMENT_LIMITS.maxCaseTotalBytes - o.usedBytes);
  return (
    `${o.what}「${o.name}」${fmtBytes(o.bytes)}，加上之后本案材料合计会超出上限。` +
    `本案佐证材料累计不能超过 ${fmtBytes(ATTACHMENT_LIMITS.maxCaseTotalBytes)}，` +
    `目前已用 ${fmtBytes(o.usedBytes)}，还剩 ${fmtBytes(remain)}。` +
    `请压缩后再传、或只传其中最关键的几件；确需更多空间请联系协会工作人员。`
  );
}

/**
 * 历史用量**读不出来**。
 *
 * ★ 这一句对应的行为是**拒绝**，不是放行。理由写在这里免得下一个人「优化」掉：
 *   读不到就当 0，等于这道闸在存储抖动时自动消失 —— 而存储抖动恰恰是最可能
 *   同时伴随大批量重传的时刻。宁可这一次传不上去，也不许闸门静默失效。
 */
export function msgCaseQuotaUnknown(what: string, reasons: readonly string[]): string {
  return (
    `暂时无法核对本案已上传材料的总量，为避免超出 ${fmtBytes(ATTACHMENT_LIMITS.maxCaseTotalBytes)} 的` +
    `单案上限，${what}本次未予接收（**不是**已经超限）。请稍后重试；` +
    `如果反复失败请联系协会工作人员。原因：${reasons.join('；')}`
  );
}

/**
 * 一次提交的总量超了请求体闸（前端拦，后端撞到的是英文 413）。
 *
 * @param usedRawBytes 已选材料的**原文**合计（不是 base64 长度，见 `TOTAL_RAW_BUDGET_BYTES`）
 */
export function msgBatchOverBudget(name: string, bytes: number, usedRawBytes: number): string {
  const remain = Math.max(0, TOTAL_RAW_BUDGET_BYTES - usedRawBytes);
  return (
    `「${name}」${fmtBytes(bytes)} 加不进来了：一次提交的材料合计不能超过 ` +
    `${fmtBytes(TOTAL_RAW_BUDGET_BYTES)}，当前已选 ${fmtBytes(usedRawBytes)}、还能再加 ${fmtBytes(remain)}。` +
    `请删掉几件体积大的，或者分两次提交（分次提交不影响办理，每一次都会独立留存）。`
  );
}

/** 界面上「最多几件、单件多大、能传什么」那句提示，各入口同一句。 */
export function attachmentLimitHint(): string {
  return (
    `最多 ${ATTACHMENT_LIMITS.maxAttachments} 件、单件不超过 ` +
    `${fmtBytes(ATTACHMENT_LIMITS.maxAttachmentBytes)}，一次提交合计不超过 ` +
    `${fmtBytes(TOTAL_RAW_BUDGET_BYTES)}；可传${ATTACHMENT_KINDS_HINT}。`
  );
}

// ────────────────────────────────────────────────────────────────────────
// 四、纯校验（前后端共用同一份；前端拦一道只为让人少跑一趟，闸门在后端）
// ────────────────────────────────────────────────────────────────────────

/**
 * 类型是否可收。返回空串＝通过；非空＝**直接给人看的中文原因**。
 *
 * @param name 文件名。传**消毒后**的名字最稳（落盘用的就是它），传原名也行 ——
 *             `sanitize()` 的白名单不动 `.` 与字母数字，扩展名不会被它改形。
 */
export function checkAttachmentType(what: string, name: string, _contentType?: string): string {
  /**
   * ★★ 2026-09-05 巡检改动：**扩展名是唯一的闸，不再拿客户端 MIME 拒收** ★★
   *
   * 原来这里还有一道 MIME 白名单，出了两个问题：
   *
   * ① **误拒合法照片**。`image/jpg`（非标准但真实存在）、`video/mpeg`、`text/csv`
   *    都不在白名单里，于是扩展名明明合法的文件被顶回来。证据以照片为主，
   *    这条挡的是真实用户。
   * ② **话术自相矛盾**。MIME 分支失败时复用了按扩展名措辞的
   *    `msgAttachmentTypeRejected()`，用户看到的是「.jpg 文件暂不支持。
   *    可以传的是：…（.jpg、.jpeg、…）」—— 说不支持 .jpg，下一句又说可以传 .jpg，
   *    照着提示改不动。
   *
   * 那道闸原本承担的**安全**职责（防 `x.jpg` 自称 `text/html` 后被 inline 打开
   * 造成存储型 XSS）已经换了更硬的做法：落盘的 Content-Type 一律由
   * `storedMimeForName()` 按扩展名派生，客户端自称的 MIME **根本进不了存储**。
   * 换句话说，安全性不降反升 —— 原来还依赖「客户端老实报 MIME」，现在不依赖了。
   *
   * 形参留着不删：三处调用方都在传第三个实参，签名改了要一起动，
   * 而它已经不参与判定，故加下划线标明「收下但不用」。
   */
  const ext = attachmentExt(name);
  if (!ALLOWED_ATTACHMENT_EXTS.includes(ext)) return msgAttachmentTypeRejected(what, name);
  return '';
}

/**
 * 单件体积是否可收。返回空串＝通过。
 * @param bytes **解码后**的原文字节数（不是 base64 串长度，那个胖 4/3）
 */
export function checkAttachmentBytes(what: string, name: string, bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return msgAttachmentEmpty(what, name);
  if (bytes > ATTACHMENT_LIMITS.maxAttachmentBytes) {
    return msgAttachmentTooLarge(what, name, bytes);
  }
  return '';
}

/** 件数是否可收。返回空串＝通过。 */
export function checkAttachmentCount(what: string, count: number): string {
  if (count > ATTACHMENT_LIMITS.maxAttachments) return msgTooManyAttachments(what, count);
  return '';
}
